// The recorder engine's state half (recorderEngine.ts is its capture and
// public half): what it holds, writing take records to the device in order,
// taking over what a closed page left (and asking "Carry on?" until the
// take finishes on its own), what happens once the server has a take (or
// says it holds no audio), and the snapshot screens and the Mac app read.

import type { Meeting } from "@/api/meetings";
import type { RecordingMessage } from "@/native/desktopRecording";
import { NOTHING_RECORDED, shellRecording } from "./recorderModel";
import { IDLE, snapshotOf, type CarryOnOffer, type RecorderSnapshot } from "./recorderSnapshot";
import type { RecorderStore, TakeRecord } from "./recorderStore";
import type { EngineDeps, RecorderDiag, Runtime } from "./recorderTypes";
import { Uploader } from "./recorderUpload";

export type { CarryOnOffer, KeptAudio, RecorderPhase, RecorderSnapshot } from "./recorderSnapshot";

export interface StartOptions {
  meetingId: string | null;
  title: string | null;
  meetingTitle?: string | null;
  meetingEndAt?: string | null;
  continueRecording?: boolean;
  /** "Call on this computer": record the call's sound (a shared tab or screen) mixed with the microphone. */
  callSound?: boolean;
}

export const iso = (ms: number) => new Date(ms).toISOString();
/** The take has been filed with the server (it has, or had, an upload). */
export const filed = (r: TakeRecord) => !!(r.upload || r.uploads?.length);
export const lockName = (id: string) => `corvus-recording-${id}`;

export abstract class RecorderCore {
  protected rts = new Map<string, Runtime>();
  protected listeners = new Set<() => void>();
  protected snap: RecorderSnapshot = IDLE;
  protected customerId: string | null = null;
  /** Who is signed in (recorderModel.ownerKey) and whether it is a child: only their takes are shown, sent or offered. */
  protected owner = "";
  protected family = false;
  protected bound = false;
  /** The last look in the store took over every unfinished take of this sign-in (I3: the Mac app's `bound`). */
  protected adopted = false;
  /** Flushes whose slice is written to the device (I3: the Mac app's `flushed`). */
  protected flushes = 0;
  protected storeRef: RecorderStore | null = null;
  private storeP: Promise<RecorderStore> | null = null;
  private reopening = false;
  protected uploader: Uploader;
  protected live: Runtime | null = null;
  protected requesting = false;
  protected failure: string | null = null;
  protected notice: string | null = null;
  protected offer: CarryOnOffer | null = null;
  protected after: Meeting | null = null;
  protected afterPoll: number | null = null;
  protected meetings = new Map<string, Meeting>();
  /** The meeting each take of this page was filed under, kept after the take is gone (a carry-on follows it). */
  protected takeMeetings = new Map<string, string>();
  protected kept = new Map<string, TakeRecord>();
  /**
   * The takes of this account the store held at the last look (not finished):
   * whether each was filed with the server, and under which meeting. A take
   * carried on from one of them waits for it, even before it is taken over.
   */
  protected listed = new Map<string, { filed: boolean; meetingId: string | null }>();
  /** Takes that left this page (finished, dropped, kept): a look in the store made just before never takes one over again. */
  protected left = new Set<string>();
  /** Take records whose last write to the store failed (until one succeeds). */
  protected recordFailed = new Set<string>();
  protected level = 0;
  protected lastLoudAt = 0;
  protected tick: number | null = null;
  protected rescan: number | null = null;
  protected heldTimers = new Map<string, number>();
  protected saves = new Map<string, Promise<void>>();
  protected exiting = false;
  private storedCount: number | null = null;
  private scanned = false;
  private recountQueued = false;

  constructor(protected deps: EngineDeps) {
    this.uploader = new Uploader({
      deps,
      store: () => this.ensureStore(),
      save: (rt) => this.save(rt),
      changed: () => this.changed(),
      meetingSeen: (rt, m) => {
        this.meetings.set(rt.rec.id, m);
        this.noteMeeting(rt.rec.id, m.id);
        this.changed();
      },
      finished: (rt, m) => this.finished(rt, m),
      dropped: (rt) => this.dropped(rt),
      noAudio: (rt) => this.noAudio(rt),
      meetingOf: (id) => this.meetingOf(id),
      owns: (rt) => this.owns(rt.rec),
    });
  }

  /** A take (or offer) of this sign-in: its workspace, and recorded by this person (a record from before owners were kept: an operator's). */
  protected owns(r: { customerId: string; owner?: string }): boolean {
    if (!this.customerId || r.customerId !== this.customerId) return false;
    return r.owner === undefined ? !this.family : r.owner === this.owner;
  }

  /** The "Carry on?" question, when it is this sign-in's to answer. */
  protected myOffer(): CarryOnOffer | null {
    return this.offer && this.owns(this.offer) ? this.offer : null;
  }

  /** The question for a take of this page, cut short at `at`. */
  protected offerFor(rt: Runtime, at: number, reason: string, finishAt: number | null): CarryOnOffer {
    const r = rt.rec;
    return {
      takeId: r.id, customerId: r.customerId, owner: r.owner, meetingId: r.meetingId, title: this.titleOf(rt), at, reason,
      savedSec: r.audioSec, finishAt, ...(r.callSound ? { callSound: true } : {}),
    };
  }

  // ─── for screens ───

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = (): RecorderSnapshot => this.snap;

  /** The Mac app's `recording` message, or null until the store has been looked in once. */
  shellMessage(): RecordingMessage | null {
    const s = this.snap;
    if (!s.scanned) return null;
    return shellRecording({
      phase: s.shellPhase, startedAt: s.startedAt, title: s.title, unsent: s.unsent, stored: s.stored, held: s.held,
      storage: s.storage, clientRef: s.clientRef, meetingId: s.meetingId, bound: s.bound && s.adopted, flushed: s.flushed,
    });
  }

  /**
   * The meeting a take was filed under, for a take that follows it (the
   * same meeting, after it): null while it is here (or in the store) and not
   * filed yet, so the follower waits; undefined when it is gone and never
   * filed (the follower files itself).
   */
  protected meetingOf(id: string): string | null | undefined {
    const rt = this.rts.get(id);
    if (rt) return filed(rt.rec) ? rt.rec.meetingId : null;
    if (this.takeMeetings.has(id)) return this.takeMeetings.get(id);
    const l = this.listed.get(id);
    if (l) return l.filed ? l.meetingId : null;
    return undefined;
  }

  /** The newest take of this page for that meeting not filed yet: a new take on the meeting goes after it. */
  protected unfiledTakeOf(meetingId: string): string | null {
    let best: Runtime | null = null;
    for (const rt of this.rts.values()) {
      const r = rt.rec;
      if (!this.owns(r) || r.meetingId !== meetingId || r.status === "done" || r.status === "kept" || filed(r)) continue;
      if (!best || r.startedAt > best.rec.startedAt) best = rt;
    }
    return best?.rec.id ?? null;
  }

  /** Open the store (only when it is needed: a page that never records never does). */
  protected ensureStore(): Promise<RecorderStore> {
    this.storeP ??= this.deps.store().then((s) => (this.storeRef = s));
    return this.storeP;
  }

  /**
   * The device store did not open in time and memory stands in: open it again,
   * once nothing of this page lives in memory (a take kept there stays there
   * until it is finished: nothing is moved). Its takes are then looked at like
   * any other (the caller adopts them).
   */
  protected async reopenUnopenedStore(): Promise<void> {
    const store = await this.ensureStore();
    if (!store.unopened || this.reopening) return;
    this.reopening = true;
    try {
      const held = await store.listTakes().catch(() => null);
      if (held === null || held.length > 0 || this.live || this.requesting || this.rts.size > 0 || this.kept.size > 0) return;
      if (this.storeRef !== store) return;
      this.storeP = null;
      this.storeRef = null;
      await this.recount();
    } finally {
      this.reopening = false;
    }
  }

  /** Look in the store: how many takes it holds (for the Mac app's "may hold audio" mark). */
  async scan(): Promise<void> {
    await this.ensureStore();
    await this.recount();
    this.scanned = true;
    this.changed();
  }

  private async recount(): Promise<void> {
    const store = await this.ensureStore();
    if (store.kind !== "device") {
      this.storedCount = null;
      return;
    }
    try {
      this.storedCount = (await store.listTakes()).length;
    } catch {
      this.storedCount = null;
    }
  }

  /** After a write to the store: count again (one count at a time, the last write always counted). */
  protected recountSoon(): void {
    if (this.recountQueued) return;
    this.recountQueued = true;
    void Promise.resolve().then(async () => {
      this.recountQueued = false;
      await this.recount();
      this.changed();
    });
  }

  protected diag(rt: Runtime, event: string, extra: Partial<RecorderDiag> = {}): void {
    const r = rt.rec;
    // Sent with the signed-in person's session: never about another person's take.
    if (!this.owns(r)) return;
    this.deps.api.diag(r.customerId, {
      event, meeting_id: r.meetingId, upload_id: r.upload?.uploadId ?? null, client_ref: r.id, segment: r.segment,
      parts: r.nextSeq, seconds: Math.round(r.audioSec), ...extra,
    });
  }

  /** Audio Corvus would not add, saved as a file: forget it here (the person kept it). */
  discardKept(takeId: string): void {
    const t = this.kept.get(takeId);
    if (!t || !this.owns(t) || !this.kept.delete(takeId)) return;
    void this.ensureStore().then((s) => s.deleteTake(takeId)).catch(() => undefined).then(() => this.recountSoon());
    this.changed();
  }

  /**
   * What is still on the device of a take, to save as a file: the one named,
   * else the one the server refuses, else the one shown, else the one held
   * for "Carry on", else one the server would not add parts of.
   */
  async localAudio(takeId?: string): Promise<{ blob: Blob; partial: boolean } | null> {
    const mine = this.mine();
    const offer = this.myOffer();
    const found = takeId
      ? this.rts.get(takeId)
      : mine.find((r) => r.waiting === "refused") ?? this.current() ?? (offer ? this.rts.get(offer.takeId) : undefined);
    const rt = found && this.owns(found.rec) ? found : undefined;
    const kept = [...this.kept.values()].filter((t) => this.owns(t));
    const rec = rt?.rec ?? kept.find((t) => t.id === takeId) ?? (takeId ? undefined : kept[0]);
    if (!rec) return null;
    const store = await this.ensureStore();
    const seqs = await store.listPartSeqs(rec.id);
    const parts: Blob[] = [];
    for (const s of seqs) {
      const p = rt?.blobs.get(s) ?? (await store.getPart(rec.id, s))?.data;
      if (p) parts.push(p);
    }
    if (!parts.length) return null;
    return { blob: new Blob(parts, { type: rec.mime }), partial: rec.sentBelow > 1 };
  }

  protected close(rt: Runtime): void {
    const t = this.heldTimers.get(rt.rec.id);
    if (t !== undefined) this.deps.clock.clearTimeout(t);
    this.heldTimers.delete(rt.rec.id);
    rt.rec.status = "closing";
    rt.finalIn = true;
    this.save(rt);
    void this.uploader.pump(rt);
  }

  /**
   * The take's meeting is known: a take carried on from it (not filed yet) goes into the
   * same one, and the take it carries on may now finish (its follower is filed first, so
   * the meeting gets one set of notes).
   */
  private noteMeeting(takeId: string, meetingId: string): void {
    this.takeMeetings.set(takeId, meetingId);
    for (const f of this.rts.values()) {
      if (f.rec.finishAfter === takeId) this.kick(f);
      if (f.rec.followsTake !== takeId || filed(f.rec)) continue;
      f.rec.meetingId = meetingId;
      this.save(f);
      this.kick(f);
    }
  }

  /** A take waiting for another one (a retry timer set): try again now. */
  protected kick(rt: Runtime): void {
    if (rt.timer === null) return;
    this.deps.clock.clearTimeout(rt.timer);
    rt.timer = null;
    rt.attempt = 0;
    void this.uploader.pump(rt);
  }

  /** The take leaves this page: its timers, its lock, its runtime. */
  protected retire(rt: Runtime): void {
    const { clock } = this.deps;
    if (rt.timer !== null) clock.clearTimeout(rt.timer);
    rt.timer = null;
    const t = this.heldTimers.get(rt.rec.id);
    if (t !== undefined) clock.clearTimeout(t);
    this.heldTimers.delete(rt.rec.id);
    rt.releaseLock?.();
    this.rts.delete(rt.rec.id);
    this.left.add(rt.rec.id);
  }

  /** Remove a take from the device, after any write still queued for it (so nothing puts it back). */
  private forget(id: string, keep: number[] = []): void {
    const queued = this.saves.get(id) ?? Promise.resolve();
    this.saves.delete(id);
    void queued
      .then(() => this.ensureStore())
      .then(async (s) => {
        if (!keep.length) return s.deleteTake(id);
        for (const seq of await s.listPartSeqs(id)) if (!keep.includes(seq)) await s.deletePart(id, seq);
      })
      .catch(() => undefined)
      .then(() => this.recountSoon());
    this.recordFailed.delete(id);
  }

  protected finished(rt: Runtime, m: Meeting): void {
    const r = rt.rec;
    this.retire(rt);
    this.meetings.set(r.id, m);
    this.noteMeeting(r.id, m.id);
    if (r.keptParts?.length) {
      // The server held other audio for these parts: they stay here (with the header) to be saved as a file.
      r.status = "kept";
      this.kept.set(r.id, r);
      const copy: TakeRecord = { ...r, interruptions: r.interruptions.map((i) => ({ ...i })) };
      const queued = (this.saves.get(r.id) ?? Promise.resolve()).then(() => this.ensureStore()).then((s) => s.putTake(copy)).catch(() => undefined);
      this.saves.set(r.id, queued);
      this.forget(r.id, [0, ...r.keptParts]);
    } else {
      r.status = "done";
      this.forget(r.id);
    }
    // A carry-on after this take goes into its meeting, even once the take is gone from here.
    if (this.offer?.takeId === r.id) this.offer = { ...this.offer, meetingId: m.id };
    if (rt.shown && !this.live && !this.closingShown()) {
      this.after = m;
      this.watchTranscription(r.customerId, m.id);
    }
    this.changed();
  }

  /** A take that holds no audio (the microphone went before the first piece): nothing to register or finish. */
  protected dropped(rt: Runtime, event = "nothing_recorded"): void {
    const r = rt.rec;
    r.status = "done";
    this.retire(rt);
    this.forget(r.id);
    this.diag(rt, event);
    // A take after this one now follows what this one followed (the same meeting, in order).
    for (const f of this.rts.values()) {
      if (f.rec.followsTake === r.id) {
        f.rec.followsTake = r.followsTake;
        f.rec.meetingId ??= r.meetingId;
        this.save(f);
        void this.uploader.pump(f);
      }
      if (f.rec.finishAfter === r.id) {
        // It waited for this one to be filed; nothing carries it on now: it finishes.
        f.rec.finishAfter = null;
        this.save(f);
        this.kick(f);
        void this.uploader.pump(f);
      }
    }
    const lead = r.followsTake;
    const cut = r.interruptions[r.interruptions.length - 1];
    if (this.offer?.takeId === r.id) this.offer = null;
    if (lead && cut && !cut.resumed_at && !this.live && !this.requesting && !this.offer) {
      // A carry-on that got nothing (the microphone went again at once): say the recording stopped, offer Carry on again.
      const prev = this.rts.get(lead);
      this.offer = {
        takeId: lead, customerId: r.customerId, owner: r.owner, meetingId: prev?.rec.meetingId ?? this.takeMeetings.get(lead) ?? r.meetingId,
        title: prev ? this.titleOf(prev) : this.titleOf(rt), at: Date.parse(cut.at) || this.deps.clock.now(),
        reason: cut.reason || "microphone_lost", savedSec: prev?.rec.audioSec ?? 0, finishAt: null,
        ...(prev?.rec.callSound ?? r.callSound ? { callSound: true } : {}),
      };
      this.notice = null;
    }
    if (rt.shown && !lead && !this.live && !this.requesting) this.notice = NOTHING_RECORDED;
    this.changed();
  }

  /** Corvus holds no audio for the take (empty_recording): recorderTakes.ts. */
  protected abstract noAudio(rt: Runtime): Promise<void>;

  protected watchTranscription(customerId: string, meetingId: string): void {
    if (this.afterPoll !== null) this.deps.clock.clearInterval(this.afterPoll);
    this.afterPoll = this.deps.clock.setInterval(async () => {
      try {
        const m = await this.deps.api.meeting(customerId, meetingId);
        if (!this.after || this.after.id !== meetingId) return;
        this.after = m;
        if (m.recording_status === "ready" || m.recording_status === "failed") this.clearPoll();
        this.changed();
      } catch { /* transient: keep looking */ }
    }, 5_000);
  }

  protected clearPoll(): void {
    if (this.afterPoll !== null) this.deps.clock.clearInterval(this.afterPoll);
    this.afterPoll = null;
  }

  protected clearAfter(): void {
    this.clearPoll();
    this.after = null;
  }

  /** Writes of one take's record, in order. */
  protected save(rt: Runtime): void {
    const id = rt.rec.id;
    const copy: TakeRecord = { ...rt.rec, interruptions: rt.rec.interruptions.map((i) => ({ ...i })) };
    const prev = this.saves.get(id) ?? Promise.resolve();
    const next = prev
      .then(() => this.ensureStore())
      .then((s) => (rt.rec.status === "done" || rt.rec.status === "kept" ? undefined : s.putTake(copy)))
      .then(() => {
        // A write that failed once and now works: the device keeps it again (decisions D1: "memory" only while it does not).
        if (this.recordFailed.delete(id)) this.changed();
        this.recountSoon();
      })
      .catch(() => {
        if (!this.recordFailed.has(id)) {
          this.recordFailed.add(id);
          this.changed();
        }
      });
    this.saves.set(id, next);
  }

  // ─── the snapshot ───

  protected titleOf(rt: Runtime): string {
    return rt.rec.meetingTitle || rt.rec.title || "the meeting";
  }

  /** This sign-in's takes still in hand (another person's on this page are never shown, sent or offered). */
  protected mine(): Runtime[] {
    return [...this.rts.values()].filter((rt) => this.owns(rt.rec) && rt.rec.status !== "done");
  }

  /** This sign-in's take being sent and finished that the screens show. */
  protected closingShown(): Runtime | null {
    let best: Runtime | null = null;
    for (const rt of this.mine()) {
      if (rt.rec.status !== "closing") continue;
      if (!best || (rt.shown && !best.shown) || (rt.shown === best.shown && rt.rec.startedAt > best.rec.startedAt)) best = rt;
    }
    return best;
  }

  /** The take the screens show: this sign-in's live one, else its closing one (none while another person's records). */
  protected current(): Runtime | null {
    if (this.live) return this.owns(this.live.rec) ? this.live : null;
    return this.closingShown();
  }

  /** Something captured lives only in this page now: no device storage, or a write to it failed and has not been made since. */
  protected onlyInMemory(): boolean {
    if (this.storeRef?.kind === "memory" || this.recordFailed.size > 0) return true;
    for (const rt of this.rts.values()) if (rt.unstored.size > 0) return true;
    return false;
  }

  protected changed(): void {
    this.snap = snapshotOf({
      now: this.deps.clock.now(), live: this.live, cur: this.current(), mine: this.mine(),
      unsentOf: (rt) => this.uploader.unsent(rt), titleOf: (rt) => this.titleOf(rt), after: this.after,
      requesting: this.requesting, failure: this.failure, notice: this.notice, offer: this.myOffer(), rts: this.rts,
      kept: [...this.kept.values()].filter((t) => this.owns(t)), meetings: this.meetings,
      level: this.level, lastLoudAt: this.lastLoudAt, storedCount: this.storedCount, scanned: this.scanned,
      storage: this.onlyInMemory() ? "memory" : "device", bound: this.bound, adopted: this.adopted, flushed: this.flushes,
      callSound: this.live?.stream?.callSound?.state() ?? null,
      callSilent: this.live?.stream?.callSound?.callSilent?.() ?? false,
      callVia: this.live?.stream?.callSound ? this.live.stream.callSound.via ?? "share" : null, callLost: this.live?.stream?.callSound?.lost?.() ?? null,
    });
    this.listeners.forEach((fn) => fn());
  }
}
